import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Avatar, Button, Card, message, Popconfirm, Row, Table, Tabs, Tag, Tooltip, Typography } from 'antd'
import TabPane from 'antd/es/tabs/TabPane'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { formatDate, formatDateString, formatToTL, hasRole } from '../../utils'
import ProposalService from '../../services/ProposalService'
import { PageableResponse } from '../../types/pagination'
import { getProposalFollowUpLink } from '../../utils/proposalLinks'
import { buildOpportunityProposalCreateLink } from './opportunityLinks'
import type { OpportunitySource } from './opportunityLinks'
import { getProposalRenewalGroupKey } from '../../utils/proposalRenewalMulti'

const DEFAULT_PAGE_SIZE = 20
const ANNIVERSARY_WITHIN_DAYS = 60

type ProposalRow = {
  id?: number
  company?: {
    id?: number
    name?: string
    account?: {
      id?: number
      roles?: Array<{
        name?: string
      }>
    }
  }
  address?: {
    id?: number
    title?: string
    state?: string
    city?: string
  }
  proposalNumber?: string
  statusId?: number
  negativeReason?: {
    text?: string
  }
  negativeReasonText?: string
  totalPrice?: number
  createdAt?: string
  approvedPlanningStartDate?: string
  approvedPlanningEndDate?: string
  renewalDate?: string
  remainingDays?: number
}

type OpportunityTabKey = '1' | '2'

const getOpportunityTabKey = (tab?: string | null): OpportunityTabKey => (
  tab === '2' ? '2' : '1'
)

const getRemainingDays = (record: ProposalRow): number => {
  if (typeof record.remainingDays === 'number') {
    return record.remainingDays
  }

  if (!record.renewalDate) {
    return Number.POSITIVE_INFINITY
  }

  return dayjs(record.renewalDate).startOf('day').diff(dayjs().startOf('day'), 'day')
}

const getRemainingDaysLabel = (remainingDays: number): string => {
  if (!Number.isFinite(remainingDays)) {
    return '-'
  }

  if (remainingDays === 0) {
    return 'Bugün'
  }

  if (remainingDays > 0) {
    return `${remainingDays} gün kaldı`
  }

  return `${Math.abs(remainingDays)} gün geçti`
}

const getRemainingDaysStatusColor = (remainingDays: number): string | undefined => {
  if (!Number.isFinite(remainingDays)) {
    return undefined
  }

  if (remainingDays <= 15) {
    return 'red'
  }

  if (remainingDays <= 30) {
    return 'gold'
  }

  return 'green'
}

export const Opportunity = () => {
  const { Title, Text } = Typography
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [rolesResolved, setRolesResolved] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isCentralUser, setIsCentralUser] = useState(false)
  const [activeKey, setActiveKey] = useState<OpportunityTabKey>(() => getOpportunityTabKey(searchParams.get('tab')))
  const [upcomingData, setUpcomingData] = useState<ProposalRow[]>([])
  const [upcomingLoading, setUpcomingLoading] = useState(false)
  const [upcomingPagination, setUpcomingPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 })
  const [renewableData, setRenewableData] = useState<ProposalRow[]>([])
  const [renewableLoading, setRenewableLoading] = useState(false)
  const [renewablePagination, setRenewablePagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 })
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedProposalRows, setSelectedProposalRows] = useState<ProposalRow[]>([])
  const [selectionGroupKey, setSelectionGroupKey] = useState<string | null>(null)
  const hasFetchedUpcoming = useRef(false)
  const hasFetchedRenewable = useRef(false)

  useEffect(() => {
    const isUserAdmin = hasRole('ADMIN')
    setIsAdmin(isUserAdmin)

    const isUserCentralUser = hasRole('CENTRAL_USER')
    setIsCentralUser(isUserCentralUser)

    setRolesResolved(true)
  }, [])

  const fetchOpportunityProposals = useCallback(async (tabKey: OpportunityTabKey, page: number, pageSize: number) => {
    const isUpcomingTab = tabKey === '1'
    const setLoading = isUpcomingTab ? setUpcomingLoading : setRenewableLoading
    const setData = isUpcomingTab ? setUpcomingData : setRenewableData
    const setPagination = isUpcomingTab ? setUpcomingPagination : setRenewablePagination
    const fetchedRef = isUpcomingTab ? hasFetchedUpcoming : hasFetchedRenewable

    setLoading(true)
    fetchedRef.current = true
    try {
      const params = {
        page: Math.max(page - 1, 0),
        size: pageSize,
        renewable: true,
        anniversaryWithinDays: ANNIVERSARY_WITHIN_DAYS,
        statuIds: isUpcomingTab ? '4' : '2',
        sort: 'renewalDate',
        direction: 'asc',
      }

      const fetchFn = (isAdmin || isCentralUser) ? ProposalService.getAll : ProposalService.getMe
      const response = await fetchFn(params)
      const data: PageableResponse<ProposalRow> = response?.data

      setData(data?.content ?? [])
      setPagination({
        current: (data?.number ?? params.page) + 1,
        pageSize: data?.size ?? pageSize,
        total: data?.totalElements ?? 0,
      })
    } catch (error) {
      console.error('Fırsat teklifleri yüklenirken hata oluştu:', error)
      fetchedRef.current = false
    } finally {
      setLoading(false)
    }
  }, [isAdmin, isCentralUser])

  useEffect(() => {
    if (!rolesResolved) {
      return
    }
    if (activeKey === '1' && !hasFetchedUpcoming.current) {
      fetchOpportunityProposals('1', 1, DEFAULT_PAGE_SIZE)
    }
    if (activeKey === '2' && !hasFetchedRenewable.current) {
      fetchOpportunityProposals('2', 1, DEFAULT_PAGE_SIZE)
    }
  }, [activeKey, fetchOpportunityProposals, rolesResolved])

  const clearSelection = useCallback(() => {
    setSelectedRowKeys([])
    setSelectedProposalRows([])
    setSelectionGroupKey(null)
  }, [])

  useEffect(() => {
    const nextKey = getOpportunityTabKey(searchParams.get('tab'))

    if (nextKey !== activeKey) {
      clearSelection()
      setActiveKey(nextKey)
    }
  }, [activeKey, clearSelection, searchParams])

  const getCreateOfferDisabledReason = useCallback((record: ProposalRow): string | null => {
    const companyOwnerIsCustomer = record?.company?.account?.roles?.some(
      (role) => role?.name === 'CUSTOMER',
    )

    if ((isAdmin || isCentralUser) && companyOwnerIsCustomer) {
      return 'Firma sahibi müşteri rolünde olduğu için teklif oluşturulamaz.'
    }

    return null
  }, [isAdmin, isCentralUser])

  const getSelectionDisabledReason = useCallback((record: ProposalRow): string | null => {
    const createOfferDisabledReason = getCreateOfferDisabledReason(record)

    if (createOfferDisabledReason) {
      return createOfferDisabledReason
    }

    const recordGroupKey = getProposalRenewalGroupKey(record)

    if (record.id === undefined || !recordGroupKey) {
      return 'Teklifin firma veya lokasyon bilgisi eksik.'
    }

    if (selectionGroupKey && recordGroupKey !== selectionGroupKey) {
      return 'Aynı firma ve aynı lokasyondaki teklifler seçilebilir.'
    }

    return null
  }, [getCreateOfferDisabledReason, selectionGroupKey])

  const handleTableChange = (pagination: any) => {
    const current = pagination?.current ?? 1
    const pageSize = pagination?.pageSize ?? DEFAULT_PAGE_SIZE
    fetchOpportunityProposals(activeKey, current, pageSize)
  }

  const handleTabChange = (key: string) => {
    const nextKey = key as OpportunityTabKey
    clearSelection()
    setActiveKey(nextKey)
    setSearchParams({ tab: nextKey })
    if (nextKey === '1') {
      fetchOpportunityProposals('1', 1, DEFAULT_PAGE_SIZE)
    }
    if (nextKey === '2') {
      fetchOpportunityProposals('2', 1, DEFAULT_PAGE_SIZE)
    }
  }

  const columns = useMemo(() => {
    const baseColumns = [
    {
      title: 'Firma Adı',
      dataIndex: ['company', 'name'],
      key: 'companyName',
      render: (text: string, record: ProposalRow) => {
        const companyName = text ?? record?.company?.name ?? ''
        const combinedText = `${record?.address?.state || ''}${record?.address?.state && record?.address?.city ? '/' : ''}${record?.address?.city || ''}`
        const controlTitle = record?.address?.title

        if (!companyName) return <span>-</span>

        return (
          <div className="d-flex" style={{ alignItems: 'center' }}>
            <div className="avatar-image-logo" style={{ marginRight: 10 }}>
              <Avatar
                className="avatar-image-logo"
                style={{ backgroundColor: '#f0f0f0', color: '#000', cursor: 'pointer' }}
                onClick={() => record?.company?.id && navigate(`/companies/${record.company.id}`)}
              >
                {companyName.charAt(0).toUpperCase()}
              </Avatar>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <Tooltip
                placement="topLeft"
                title={
                  <>
                    {companyName.toUpperCase()} <br />
                    {controlTitle && <>{controlTitle}<br /></>}
                    {combinedText}
                  </>
                }
              >
                <Typography.Text
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    width: '220px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  onClick={() => record?.company?.id && navigate(`/companies/${record.company.id}`)}
                >
                  {companyName.toUpperCase()}
                </Typography.Text>
              </Tooltip>
              {controlTitle && (
                <div style={{ color: '#666', fontSize: 12 }}>
                  {controlTitle.length > 60 ? (
                    <Tooltip title={controlTitle}>
                      <span>{controlTitle.slice(0, 60)}...</span>
                    </Tooltip>
                  ) : (
                    <span>{controlTitle}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      },
    },
    {
      title: 'Önceki Teklif Tutarı',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (value: number) => value != null ? (
        <div className="price-tooltip width-120px">
          <span><b>{formatToTL(value)}</b></span>
        </div>
      ) : <span>-</span>,
    },
    {
      title: 'Önceki Teklif Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => value ? (
        <div className="width-120px">
          <Typography.Text>{formatDate(value)}</Typography.Text>
        </div>
      ) : <span>-</span>,
    },
    {
      title: 'Önceki Teklif',
      dataIndex: 'proposalNumber',
      key: 'proposalNumber',
      render: (value: string, record: ProposalRow) => {
        const proposalNumber = value?.trim()
        if (!proposalNumber) return <span>-</span>

        const targetUrl = activeKey === '2'
          ? `/proposals?search=${encodeURIComponent(proposalNumber)}`
          : getProposalFollowUpLink(proposalNumber, record?.statusId)
        const proposalNumberElement = targetUrl ? (
          <Link to={targetUrl} style={{ color: 'blue' }}>
            {proposalNumber}
          </Link>
        ) : (
          <span style={{ color: 'blue' }}>{proposalNumber}</span>
        )
        const negativeReasonMessages: string[] = []
        if (record?.negativeReason?.text) {
          negativeReasonMessages.push(record.negativeReason.text)
        }
        if (record?.negativeReasonText) {
          negativeReasonMessages.push(record.negativeReasonText)
        }
        const negativeReasonTooltipContent =
          negativeReasonMessages.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {negativeReasonMessages.map((message, index) => (
                <span key={index}>{message}</span>
              ))}
            </div>
          ) : null

        if (activeKey === '2' && record?.statusId === 2 && negativeReasonTooltipContent) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {proposalNumberElement}
              <Tooltip title={negativeReasonTooltipContent}>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </span>
          )
        }

        return proposalNumberElement
      },
    },
    ...(activeKey === '1'
      ? [
        {
          title: 'Kontrol Tarihleri',
          key: 'controlDates',
          render: (_: any, record: ProposalRow) => {
            const startDate = record?.approvedPlanningStartDate
              ? formatDateString(record.approvedPlanningStartDate)
              : undefined
            const endDate = record?.approvedPlanningEndDate
              ? formatDateString(record.approvedPlanningEndDate)
              : undefined

            if (!startDate && !endDate) {
              return <span>-</span>
            }

            return (
              <div className="width-120px">
                {startDate && <Typography.Text>{startDate}</Typography.Text>}
                {endDate && (
                  <Typography.Text style={{ display: 'block' }}>
                    {endDate}
                  </Typography.Text>
                )}
              </div>
            )
          },
        },
      ]
      : []),
    {
      title: 'Planlanan Teklif Hazırlama Tarihi',
      dataIndex: 'renewalDate',
      key: 'renewalDate',
      defaultSortOrder: 'ascend' as const,
      sorter: (first: ProposalRow, second: ProposalRow) => getRemainingDays(first) - getRemainingDays(second),
      render: (value: string, record: ProposalRow) => {
        if (!value) {
          return <span>-</span>
        }

        const remainingDays = getRemainingDays(record)

        return (
          <div className="width-120px">
            <Typography.Text>{formatDateString(value)}</Typography.Text>
            <Tag
              color={getRemainingDaysStatusColor(remainingDays)}
              style={{ display: 'inline-block', marginTop: 4, marginInlineEnd: 0 }}
            >
              {getRemainingDaysLabel(remainingDays)}
            </Tag>
          </div>
        )
      },
    },
    {
      title: '',
      key: 'renew',
      render: (_: any, record: ProposalRow) => {
        const disabledReason = getCreateOfferDisabledReason(record)
        const disableCreateOffer = Boolean(disabledReason)
        const createOfferButton = (
          <Button
            type="primary"
            className="d-button width-auto"
            disabled={disableCreateOffer}
          >
            Teklif Oluştur
          </Button>
        )

        if (disableCreateOffer) {
          return (
            <Tooltip title={disabledReason}>
              <span>{createOfferButton}</span>
            </Tooltip>
          )
        }

        return (
          <Popconfirm
            placement="left"
            title="Bu teklifin bilgileriyle yeni teklif oluşturma ekranını açmak istediğinize emin misiniz?"
            okText="Evet"
            cancelText="Hayır"
            onConfirm={() => navigate(buildOpportunityProposalCreateLink(
              record.id as number,
              activeKey === '1' ? 'upcoming-projects' : 'renewable-proposals',
            ))}
          >
            {createOfferButton}
          </Popconfirm>
        )
      },
    },
    ]

    return baseColumns
  }, [activeKey, getCreateOfferDisabledReason, navigate])

  const activeRawData = activeKey === '1' ? upcomingData : renewableData
  const activeData = useMemo(
    () => [...activeRawData].sort((first, second) => getRemainingDays(first) - getRemainingDays(second)),
    [activeRawData],
  )
  const activeLoading = activeKey === '1' ? upcomingLoading : renewableLoading
  const activePagination = activeKey === '1' ? upcomingPagination : renewablePagination
  const activeOpportunitySource: OpportunitySource = activeKey === '1' ? 'upcoming-projects' : 'renewable-proposals'
  const selectedProposalIds = useMemo(() => {
    const selectedKeySet = new Set(selectedRowKeys.map((key) => String(key)))
    const activeOrderedIds = activeData
      .filter((record) => record.id !== undefined && selectedKeySet.has(String(record.id)))
      .map((record) => record.id as number)
    const activeOrderedIdSet = new Set(activeOrderedIds.map((id) => String(id)))
    const remainingIds = selectedProposalRows
      .filter((record) => record.id !== undefined && !activeOrderedIdSet.has(String(record.id)))
      .map((record) => record.id as number)

    return [...activeOrderedIds, ...remainingIds]
  }, [activeData, selectedProposalRows, selectedRowKeys])

  const handleSelectionChange = useCallback((_: React.Key[], nextSelectedRows: ProposalRow[]) => {
    const selectableRows = nextSelectedRows.filter((record) => (
      record.id !== undefined
      && !getCreateOfferDisabledReason(record)
      && getProposalRenewalGroupKey(record)
    ))

    if (selectableRows.length === 0) {
      clearSelection()
      return
    }

    const nextGroupKey = getProposalRenewalGroupKey(selectableRows[0])
    const compatibleRows = selectableRows.filter((record) => getProposalRenewalGroupKey(record) === nextGroupKey)

    if (compatibleRows.length !== selectableRows.length) {
      message.warning('Aynı firma ve aynı lokasyondaki teklifler seçilebilir.')
    }

    setSelectionGroupKey(nextGroupKey)
    setSelectedProposalRows(compatibleRows)
    setSelectedRowKeys(compatibleRows.map((record) => record.id as number))
  }, [clearSelection, getCreateOfferDisabledReason])

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    hideSelectAll: true,
    onChange: handleSelectionChange,
    getCheckboxProps: (record: ProposalRow) => {
      const disabledReason = getSelectionDisabledReason(record)

      return {
        disabled: Boolean(disabledReason),
        title: disabledReason ?? undefined,
      }
    },
    renderCell: (_: boolean, record: ProposalRow, __: number, originNode: React.ReactNode) => {
      const disabledReason = getSelectionDisabledReason(record)

      if (!disabledReason) {
        return originNode
      }

      return (
        <Tooltip title={disabledReason}>
          <span
            onClick={(event) => {
              event.stopPropagation()
              message.warning(disabledReason)
            }}
            style={{ display: 'inline-flex' }}
          >
            {originNode}
          </span>
        </Tooltip>
      )
    },
  }), [getSelectionDisabledReason, handleSelectionChange, selectedRowKeys])

  const handleBulkCreateOffer = () => {
    if (selectedProposalIds.length === 0) {
      return
    }

    navigate(buildOpportunityProposalCreateLink(selectedProposalIds, activeOpportunitySource))
  }

  return (
    <>
      <Tabs
        activeKey={activeKey}
        onChange={handleTabChange}
        className="custom-tabs"
        tabBarStyle={{ borderBottom: 'none' }}
      >
        <TabPane
          tab={
            <Button
              type={activeKey === '1' ? 'primary' : 'default'}
              className="d-button tab-button"
            >
              Yaklaşan Projeler
            </Button>
          }
          key="1"
        >
        </TabPane>
        <TabPane
          tab={
            <Button
              type={activeKey === '2' ? 'primary' : 'default'}
              className="d-button tab-button"
            >
              Yenilenebilir Teklifler
            </Button>
          }
          key="2"
        >
        </TabPane>
      </Tabs>
      <Card className="br-30px mb-120px">
        <Row align="bottom" justify="space-between" style={{ marginBottom: 20, gap: 12 }}>
          <div>
            <Title style={{ margin: 0 }} level={4}>
              {(activeKey === '1') && 'Yaklaşan Projeler Listesi'}
              {(activeKey === '2') && 'Yenilenebilir Teklif Listesi'}
            </Title>
            <Text type="secondary"><strong>({activePagination.total})</strong> kayıt listelendi</Text>
          </div>
          {selectedProposalIds.length > 0 ? (
            <Popconfirm
              placement="left"
              title={`${selectedProposalIds.length} seçili teklifin bilgileriyle yeni teklif oluşturma ekranını açmak istediğinize emin misiniz?`}
              okText="Evet"
              cancelText="Hayır"
              onConfirm={handleBulkCreateOffer}
            >
              <Button type="primary" className="d-button width-auto">
                Çoklu Teklif Oluştur ({selectedProposalIds.length})
              </Button>
            </Popconfirm>
          ) : (
            <Button type="primary" className="d-button width-auto" disabled>
              Çoklu Teklif Oluştur
            </Button>
          )}
        </Row>
        <Table
          columns={columns}
          dataSource={activeData}
          loading={activeLoading}
          onChange={handleTableChange}
          rowSelection={rowSelection}
          pagination={{
            current: activePagination.current,
            pageSize: activePagination.pageSize,
            total: activePagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
          }}
          rowKey={(record: ProposalRow, index) => record?.id ?? `${activeKey}-${index}`}
        />
      </Card>
    </>

  )
}
